import type { PayrollRun } from "../types/payroll.types";
import { payrollItemEmployeeLabel } from "./payrollEmployeeDisplay";

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function lineSummary(
  rows: { type?: string; label?: string; amount?: number }[] | undefined,
): string {
  if (!rows?.length) return "—";
  return rows
    .map((r) => {
      const name = r.label || r.type || "Line";
      const amt =
        r.amount != null && !Number.isNaN(r.amount) ? String(r.amount) : "—";
      return `${name}: ${amt}`;
    })
    .join("; ");
}

function safeFilenamePart(s: string): string {
  return s
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 80);
}

/** UTF-8 BOM so Excel opens UTF-8 CSV correctly for non-ASCII names. */
const UTF8_BOM = "\uFEFF";

/**
 * Builds a CSV for the payroll run line items (employee rows + earnings/deductions summaries).
 */
export function buildPayrollRunCsv(run: PayrollRun): string {
  const items = run.items ?? [];
  const header = [
    "Employee ID",
    "Employee",
    "Monthly Salary",
    "Gross pay",
    "Net pay",
    "Earnings",
    "Deductions",
  ];
  const lines = [header.join(",")];
  for (const item of items) {
    const monthlySalary = item.employee?.monthlySalary ?? item.employeeSalary;
    lines.push(
      [
        item.employeeId != null ? String(item.employeeId) : "",
        csvEscape(payrollItemEmployeeLabel(item)),
        monthlySalary != null && !Number.isNaN(monthlySalary)
          ? String(monthlySalary)
          : "",
        item.grossPay != null && !Number.isNaN(item.grossPay)
          ? String(item.grossPay)
          : "",
        item.netPay != null && !Number.isNaN(item.netPay)
          ? String(item.netPay)
          : "",
        csvEscape(lineSummary(item.earnings)),
        csvEscape(lineSummary(item.deductions)),
      ].join(","),
    );
  }
  return UTF8_BOM + lines.join("\n");
}

export function downloadPayrollRunCsv(run: PayrollRun): void {
  const csv = buildPayrollRunCsv(run);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  const base = safeFilenamePart(
    `payroll-run-${run.id}_${run.periodStart ?? ""}_${run.periodEnd ?? ""}`,
  );
  a.href = url;
  a.download = `${base || `payroll-run-${run.id}`}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
